import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { CheckCircle, XCircle, Lock, Shield, Cpu, ChevronDown, ChevronUp } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/resources" className="dark-nav-link">RESOURCES</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-accordion-item ${isOpen ? 'active' : ''}`}>
      <button className="faq-accordion-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-accordion-question">{question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div className="faq-accordion-content">
          <p className="faq-answer">{answer}</p>
        </div>
      )}
    </div>
  );
};

const WalletPasswordRecoveryOwnerAuthorised = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Wallet Password Recovery: Owner-Authorised Guide (UK)</title>
        <meta name="description" content="Lost your wallet password (wallet.dat or keystore JSON)? Learn safe owner-authorised recovery steps, feasibility factors, and what to avoid. Non-custodial. Free assessment." />
        <meta name="keywords" content="wallet password recovery, wallet.dat password, keystore JSON password, Bitcoin Core password, encrypted wallet recovery, owner authorized" />
        <link rel="canonical" href="https://arcana-crypto.com/wallet-password-recovery-guide.html" />
      </Helmet>

      <Navigation />
      
      <article className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container" style={{maxWidth: '900px'}}>
          
          {/* Header */}
          <header>
            <h1 className="section-title" style={{fontSize: '42px', textAlign: 'left', marginBottom: '16px'}}>
              Wallet Password Recovery (wallet.dat / keystore): How It Works
            </h1>
            <p className="section-intro" style={{fontSize: '18px', marginBottom: '40px'}}>
              <strong>Password recovery</strong> is not "hacking". It is a controlled, <strong>owner-authorised</strong> technical 
              process that depends on what you remember and how the wallet was encrypted.
            </p>
          </header>

          {/* Key Takeaways */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Key Takeaways
            </h2>
            <ul className="column-list">
              <li>Only for the verified rightful owner.</li>
              <li>Feasibility depends on password patterns, length, and hints.</li>
              <li>Offline handling and encryption are critical.</li>
              <li>No guarantees: probability estimates after assessment.</li>
              <li>Non-custodial: you retain control of funds.</li>
            </ul>
          </section>

          {/* What to Do */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <CheckCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Do
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Preserve the original file; work from a copy.</li>
                <li>Write down <strong>password hints</strong> (patterns, old passwords, length range).</li>
                <li>Identify <strong>wallet software and version</strong> (Bitcoin Core, Electrum, Ethereum keystore, etc.).</li>
                <li>Prepare ownership evidence context.</li>
                <li>Use encrypted channels for any sensitive transfer.</li>
              </ul>
            </div>
          </section>

          {/* What to Avoid */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <XCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Avoid
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Do not upload wallet files to random services.</li>
                <li>Avoid repeated guesses if lockouts apply.</li>
                <li>Do not modify the wallet file.</li>
                <li>Do not assume GPU means instant recovery.</li>
              </ul>
            </div>
          </section>

          {/* Technical Explanation */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Cpu size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Technical Explanation (Simplified)
            </h2>
            <div className="legal-content">
              <p>
                Common approaches include <strong>dictionary-based search</strong>, <strong>pattern/rule-based search</strong>, 
                and <strong>mask attacks</strong>. For <strong>Bitcoin Core wallet.dat</strong> and <strong>Ethereum keystore JSON</strong>, 
                encryption/KDF settings materially affect speed and feasibility.
              </p>
              <p style={{marginTop: '16px'}}>
                Modern encrypted wallets use key derivation functions (KDF) like PBKDF2 or scrypt with thousands 
                of iterations, making brute-force attempts computationally expensive. <strong>GPU acceleration</strong> can 
                significantly speed up testing, but feasibility still depends on password entropy and available hints.
              </p>
              <p style={{marginTop: '16px'}}>
                <strong>Owner-authorized recovery</strong> means we work strictly with verified wallet owners using 
                controlled methodologies. This is not unauthorized "hacking" - it's legitimate technical assistance 
                for rightful asset recovery.
              </p>
            </div>
          </section>

          {/* Non-Custodial & Privacy */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Shield size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Non-Custodial & Privacy
            </h2>
            <div className="about-card">
              <p className="legal-content" style={{marginBottom: '16px'}}>
                <strong>Owner-authorised</strong> scope only, offline work, encrypted comms, data minimisation 
                and deletion aligned with GDPR principles.
              </p>
              <ul className="column-list">
                <li>Strictly non-custodial: we never access your funds</li>
                <li>Encrypted wallet file handling only</li>
                <li>Password delivered securely - you access wallet yourself</li>
                <li>Offline processing with encrypted communications</li>
                <li>Data deletion upon service completion</li>
              </ul>
            </div>
          </section>

          {/* When Recovery is Feasible */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              When Recovery is Feasible
            </h2>
            <div className="about-card">
              <p className="legal-content">
                <strong>More feasible</strong> with credible hints and non-random passwords. Patterns you remember 
                (base words, numbers, special chars, length range) significantly improve probability.
              </p>
              <p className="legal-content" style={{marginTop: '16px'}}>
                <strong>Less feasible</strong> when the password was long, completely random, and you have no hints. 
                Fully random 20+ character passwords without patterns are often not economically feasible to recover.
              </p>
            </div>
          </section>

          {/* What We Need */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              What We Need for Assessment
            </h2>
            <div className="pricing-notes">
              <ul className="column-list">
                <li>Wallet type (Bitcoin Core, Electrum, Ethereum keystore, etc.)</li>
                <li>Software/version used to create wallet</li>
                <li>Password hints (patterns, old passwords, character types, length estimate)</li>
                <li>Time constraints and urgency</li>
                <li>Ownership context (how you acquired wallet, approximate creation date)</li>
                <li>Preferred secure handling method</li>
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section style={{marginBottom: '80px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '32px'}}>
              Frequently Asked Questions
            </h2>
            <div className="faq-accordion">
              <FAQItem 
                question="Do you guarantee recovery?"
                answer="No. Password recovery is probabilistic and depends on entropy, hints, and computational feasibility."
              />
              <FAQItem 
                question="Is this legal?"
                answer="Only for verified owners, within legal boundaries. Owner authorization is required and verified."
              />
              <FAQItem 
                question="Do you need remote access?"
                answer="Typically no. You send the encrypted wallet file securely; we return the password if successful."
              />
              <FAQItem 
                question="Can you recover a fully random 20+ char password?"
                answer="Often not economically feasible without hints. Assessment determines if computational requirements are reasonable relative to wallet value."
              />
              <FAQItem 
                question="How do you protect data?"
                answer="Offline processing + encrypted communications + data minimisation + secure deletion after completion."
              />
              <FAQItem 
                question="What if the file is corrupted?"
                answer="File corruption can prevent recovery. Assessment includes file integrity verification."
              />
              <FAQItem 
                question="How long does it take?"
                answer="Hours to weeks depending on password complexity and hints. Simple patterns: hours. Complex passwords with weak hints: days to weeks."
              />
            </div>
          </section>

          {/* CTA */}
          <section className="cta-section" style={{marginTop: '80px', padding: '60px 48px'}}>
            <h2 className="cta-title" style={{fontSize: '36px'}}>
              Forgotten Your Wallet Password?
            </h2>
            <p className="cta-subtitle" style={{marginBottom: '40px'}}>
              Request a free, confidential assessment
            </p>
            <button className="btn-primary" style={{fontSize: '18px', padding: '18px 40px'}} onClick={() => window.location.href = '/#assessment'}>
              FREE PASSWORD RECOVERY ASSESSMENT
            </button>
            <p className="hero-small-text" style={{marginTop: '24px'}}>
              Owner-Authorized • Non-custodial • Confidential
            </p>
            <div style={{marginTop: '32px'}}>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '12px'}}>
                <strong>Related Services:</strong>
              </p>
              <a href="/wallet-password-recovery" className="card-link" style={{fontSize: '16px', marginRight: '20px'}}>
                Wallet Password Recovery Service →
              </a>
              <a href="/seed-phrase-recovery" className="card-link" style={{fontSize: '16px'}}>
                Seed Recovery →
              </a>
            </div>
          </section>

          {/* Breadcrumb Back */}
          <div style={{marginTop: '60px', textAlign: 'center'}}>
            <a href="/resources" className="card-link" style={{fontSize: '16px'}}>
              ← Back to All Resources
            </a>
          </div>

        </div>
      </article>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WalletPasswordRecoveryOwnerAuthorised;
